// The permission each page's data needs, as the API's [Authorize] policies enforce it (RolePermissionMapping).
export const PAGE_PERMISSIONS = {
  overview: 'analytics:view_overview',
  wallboard: 'analytics:view_wallboard',
  sla: 'analytics:view_queue_analytics',
  calls: 'analytics:view_call_log',
  insights: 'analytics:view_insights',
  callFlow: 'analytics:view_overview',
  heatmap: 'analytics:view_overview',
  agents: 'analytics:view_agent_analytics',
  queues: 'analytics:view_queue_analytics',
  reports: 'analytics:view_reports',
  settings: 'account:view_settings',
  users: 'users:view',
  directory: 'directory:manage',
};

export const EXPORT_PERMISSION = 'analytics:export_data';

// Navigation from docs/design/mockups/_build/shell-ui.js (VL.nav). `countKey` names an attention count the shell can show.
export const NAV_SECTIONS = [
  {
    title: 'Monitor',
    items: [
      { id: 'overview', label: 'Overview', icon: 'grid', to: '/overview', permission: PAGE_PERMISSIONS.overview },
      { id: 'wallboard', label: 'Wallboard', icon: 'monitor', to: '/wallboard', permission: PAGE_PERMISSIONS.wallboard },
      { id: 'sla', label: 'SLA', icon: 'target', to: '/sla', permission: PAGE_PERMISSIONS.sla },
    ],
  },
  {
    title: 'Analyze',
    items: [
      { id: 'calls', label: 'Calls', icon: 'phone', to: '/calls', permission: PAGE_PERMISSIONS.calls },
      { id: 'insights', label: 'Insights', icon: 'sparkle', to: '/insights', countKey: 'insights', permission: PAGE_PERMISSIONS.insights },
      { id: 'flow', label: 'Call flow', icon: 'flow', to: '/call-flow', permission: PAGE_PERMISSIONS.callFlow },
      { id: 'heatmap', label: 'Heatmap', icon: 'heat', to: '/heatmap', permission: PAGE_PERMISSIONS.heatmap },
      { id: 'agents', label: 'Agents', icon: 'headset', to: '/agents', permission: PAGE_PERMISSIONS.agents },
      { id: 'queues', label: 'Queues', icon: 'layers', to: '/queues', permission: PAGE_PERMISSIONS.queues },
    ],
  },
  {
    title: 'Report',
    items: [{ id: 'reports', label: 'Reports', icon: 'file', to: '/reports', permission: PAGE_PERMISSIONS.reports }],
  },
  {
    title: 'Configure',
    items: [
      { id: 'hierarchy', label: 'Hierarchy', icon: 'sitemap', to: '/admin/hierarchy', permission: PAGE_PERMISSIONS.settings },
      { id: 'communities', label: 'Communities', termLabel: 'communities', icon: 'home', to: '/admin/communities', permission: PAGE_PERMISSIONS.settings },
      { id: 'directory', label: 'Directory', icon: 'user', to: '/admin/directory', permission: PAGE_PERMISSIONS.directory },
      { id: 'taxonomy', label: 'Taxonomy', icon: 'tag', to: '/admin/taxonomy', permission: PAGE_PERMISSIONS.settings },
      { id: 'ai', label: 'AI and transcription', icon: 'cpu', to: '/admin/ai', permission: PAGE_PERMISSIONS.settings },
      { id: 'users', label: 'Users and roles', icon: 'userCog', to: '/admin/users', permission: PAGE_PERMISSIONS.users },
      { id: 'slaconfig', label: 'SLA and hours', icon: 'clock', to: '/admin/sla', permission: PAGE_PERMISSIONS.settings },
    ],
  },
];

/** An item's label; the Communities item uses the account's plural term ("Associations"). */
export function navLabel(item, accountTerms) {
  return item.termLabel && accountTerms ? accountTerms[item.termLabel] : item.label;
}

/** The nav sections and items the signed-in role can open; sections left empty are dropped. */
export function visibleNavSections(hasPermission, sections = NAV_SECTIONS) {
  return sections
    .map((section) => ({ ...section, items: section.items.filter((item) => !item.permission || hasPermission(item.permission)) }))
    .filter((section) => section.items.length > 0);
}

/** Where a signed-in user lands: the first page in the nav their role can open (not the full-screen wallboard), else the Help Center. */
export function homePath(hasPermission) {
  const first = visibleNavSections(hasPermission)
    .flatMap((s) => s.items)
    .find((item) => item.to !== '/wallboard');
  return first ? first.to : '/help';
}

// Role names from docs/design/mockups/_build/pages/20-admin-users.html, highest first.
export const ROLE_LABELS = [
  ['super_admin', 'Vertolytics super administrator'],
  ['account_admin', 'Account administrator'],
  ['manager', 'Manager'],
  ['supervisor', 'Supervisor'],
  ['executive', 'Executive'],
  ['agent', 'Agent'],
  ['board_member', 'Board member'],
];

export const roleLabel = (roles = []) => {
  const match = ROLE_LABELS.find(([role]) => roles.includes(role));
  return match ? match[1] : 'No role assigned';
};
